import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../App";
import HolidayExcused from "./HolidayExcused";
import { employeeHasRegularDayOff } from "./ScheduleView";

function RequestList() {
	// Two sections: the leave calendar and the Holiday Excused picker
	const [tab, setTab] = useState(() =>
		new URLSearchParams(window.location.search).get("tab") === "holiday"
			? "holiday"
			: "leave",
	);
	const [requests, setRequests] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [holidayDates, setHolidayDates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	// Main calendar view state
	const [viewMonth, setViewMonth] = useState(new Date());

	// Add modal state
	const [showAddModal, setShowAddModal] = useState(false);
	const [selectedEmployee, setSelectedEmployee] = useState("");
	const [selectedDates, setSelectedDates] = useState([]);
	const [reason, setReason] = useState("");
	const [leaveType, setLeaveType] = useState("regular");
	const [calendarMonth, setCalendarMonth] = useState(new Date());

	// Detail modal state (for clicking on a day)
	const [showDetailModal, setShowDetailModal] = useState(false);
	const [selectedDayLeaves, setSelectedDayLeaves] = useState([]);
	const [selectedDayDate, setSelectedDayDate] = useState("");

	useEffect(() => {
		loadData();
	}, []);

	const loadData = async () => {
		try {
			const [requestsRes, employeesRes, holidaysRes] = await Promise.all([
				axios.get(`${API_URL}/requests?status=approved`),
				axios.get(`${API_URL}/employees`),
				axios.get(`${API_URL}/holidays`),
			]);
			setRequests(requestsRes.data);
			setEmployees(employeesRes.data);
			setHolidayDates(holidaysRes.data.map((h) => h.holiday_date));
		} catch (error) {
			setError("Failed to load data");
		} finally {
			setLoading(false);
		}
	};

	const loadRequests = async () => {
		try {
			const response = await axios.get(
				`${API_URL}/requests?status=approved`,
			);
			setRequests(response.data);
		} catch (error) {
			setError("Failed to load requests");
		}
	};

	const handleDelete = async (requestId) => {
		try {
			await axios.delete(`${API_URL}/requests/${requestId}`);
			setSuccess("Leave entry deleted successfully");
			loadRequests();
			setTimeout(() => setSuccess(""), 3000);
		} catch (error) {
			setError("Failed to delete leave entry");
		}
	};

	const handleAddLeave = async () => {
		if (!selectedEmployee) {
			setError("Please select an employee");
			return;
		}
		if (selectedDates.length === 0) {
			setError("Please select at least one date");
			return;
		}

		setError("");
		try {
			// Add each selected date as approved leave
			for (const date of selectedDates) {
				await axios.post(`${API_URL}/requests/admin-add`, {
					employeeId: parseInt(selectedEmployee),
					requestDate: date,
					reason: reason || null,
					leaveType: leaveType || "regular",
					status: "approved",
				});
			}
			setSuccess(
				`Added ${selectedDates.length} leave day(s) successfully`,
			);
			setShowAddModal(false);
			setSelectedEmployee("");
			setSelectedDates([]);
			setReason("");
			setLeaveType("regular");
			loadRequests();
			setTimeout(() => setSuccess(""), 3000);
		} catch (error) {
			setError(error.response?.data?.error || "Failed to add leave");
		}
	};

	const toggleDate = (dateStr) => {
		setSelectedDates((prev) =>
			prev.includes(dateStr)
				? prev.filter((d) => d !== dateStr)
				: [...prev, dateStr].sort(),
		);
	};

	// Why a day can't be picked for leave: a holiday, or the employee's NWD
	const getBlockedReason = (day, employeeId) => {
		if (holidayDates.includes(formatDateStr(day))) return "Holiday";
		const emp = employees.find((e) => e.id === parseInt(employeeId));
		if (emp && employeeHasRegularDayOff(emp, day)) return "NWD";
		return null;
	};

	const handleEmployeeChange = (employeeId) => {
		setSelectedEmployee(employeeId);
		// Drop picked dates that are an NWD for the newly chosen employee
		setSelectedDates((prev) =>
			prev.filter(
				(dateStr) =>
					!getBlockedReason(new Date(dateStr + "T00:00:00"), employeeId),
			),
		);
	};

	// Calendar helper functions
	const getDaysInMonth = (date) => {
		const year = date.getFullYear();
		const month = date.getMonth();
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);
		const days = [];

		// Add empty cells for days before the first day of month
		for (let i = 0; i < firstDay.getDay(); i++) {
			days.push(null);
		}

		// Add all days in the month
		for (let d = 1; d <= lastDay.getDate(); d++) {
			days.push(new Date(year, month, d));
		}

		return days;
	};

	const formatDateStr = (date) => {
		const pad = (n) => String(n).padStart(2, "0");
		return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
	};

	// Main view month navigation
	const prevViewMonth = () => {
		setViewMonth(
			new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1),
		);
	};

	const nextViewMonth = () => {
		setViewMonth(
			new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1),
		);
	};

	// Add modal month navigation
	const prevMonth = () => {
		setCalendarMonth(
			new Date(
				calendarMonth.getFullYear(),
				calendarMonth.getMonth() - 1,
				1,
			),
		);
	};

	const nextMonth = () => {
		setCalendarMonth(
			new Date(
				calendarMonth.getFullYear(),
				calendarMonth.getMonth() + 1,
				1,
			),
		);
	};

	// Get leaves for a specific date
	const getLeavesForDate = (dateStr) => {
		return requests.filter((req) => req.request_date === dateStr);
	};

	// Handle clicking on a calendar day with leaves
	const handleDayClick = (dateStr, leaves) => {
		if (leaves.length > 0) {
			setSelectedDayDate(dateStr);
			setSelectedDayLeaves(leaves);
			setShowDetailModal(true);
		}
	};

	// Get calendar days for view month
	const getViewMonthDays = () => {
		const year = viewMonth.getFullYear();
		const month = viewMonth.getMonth();
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);
		const days = [];

		for (let i = 0; i < firstDay.getDay(); i++) {
			days.push(null);
		}

		for (let d = 1; d <= lastDay.getDate(); d++) {
			days.push(new Date(year, month, d));
		}

		return days;
	};

	const getLeaveTypeLabel = (type) => {
		switch (String(type || "regular").toLowerCase()) {
			case "holiday":
				return "Holiday Excused";
			case "sick":
				return "Sick Leave";
			default:
				return "Annual Leave";
		}
	};

	const getLeaveTypeBadgeClass = (type) => {
		switch (String(type || "regular").toLowerCase()) {
			case "holiday":
				return "badge-primary";
			case "sick":
				return "badge-error";
			default:
				return "badge-warning";
		}
	};

	if (loading) {
		return (
			<div className='loading-container'>
				<div className='spinner'></div>
			</div>
		);
	}

	const calendarDays = getDaysInMonth(calendarMonth);

	const tabBar = (
		<div className='flex gap-sm mb-md'>
			<button
				className={`btn ${tab === "leave" ? "btn-primary" : "btn-secondary"}`}
				onClick={() => setTab("leave")}
			>
				Leave Calendar
			</button>
			<button
				className={`btn ${tab === "holiday" ? "btn-primary" : "btn-secondary"}`}
				onClick={() => setTab("holiday")}
			>
				Holiday Excused
			</button>
		</div>
	);

	if (tab === "holiday") {
		return (
			<div>
				<div className='page-header'>
					<h1 className='page-title'>Leave Management</h1>
				</div>
				{tabBar}
				<HolidayExcused />
			</div>
		);
	}

	return (
		<div>
			<div className='page-header'>
				<h1 className='page-title'>Leave Management</h1>
				<button
					className='btn btn-primary'
					onClick={() => setShowAddModal(true)}
				>
					+ Add Leave
				</button>
			</div>

			{tabBar}

			{success && <div className='alert alert-success'>{success}</div>}
			{error && <div className='alert alert-error'>{error}</div>}

			{/* Calendar View */}
			<div className='card'>
				{/* Calendar Navigation */}
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						padding: "1rem 1.5rem",
						borderBottom: "1px solid var(--gray-200)",
					}}
				>
					<button
						className='btn btn-secondary'
						onClick={prevViewMonth}
					>
						◀ Previous
					</button>
					<h2 style={{ margin: 0, fontSize: "1.25rem" }}>
						{viewMonth.toLocaleDateString("en-US", {
							month: "long",
							year: "numeric",
						})}
					</h2>
					<button
						className='btn btn-secondary'
						onClick={nextViewMonth}
					>
						Next ▶
					</button>
				</div>

				{/* Calendar Grid */}
				<div style={{ padding: "1rem" }}>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(7, 1fr)",
							gap: "1px",
							background: "var(--gray-200)",
							border: "1px solid var(--gray-200)",
							borderRadius: "var(--radius-md)",
							overflow: "hidden",
						}}
					>
						{/* Day headers */}
						{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
							(day) => (
								<div
									key={day}
									style={{
										padding: "0.75rem",
										textAlign: "center",
										fontWeight: "600",
										fontSize: "0.875rem",
										background: "var(--gray-100)",
										color: "var(--gray-700)",
									}}
								>
									{day}
								</div>
							),
						)}

						{/* Calendar days */}
						{getViewMonthDays().map((day, idx) => {
							if (!day) {
								return (
									<div
										key={`empty-${idx}`}
										style={{
											background: "var(--gray-50)",
											minHeight: "100px",
										}}
									/>
								);
							}
							const dateStr = formatDateStr(day);
							const dayLeaves = getLeavesForDate(dateStr);
							const isToday =
								dateStr === formatDateStr(new Date());
							const isWeekend =
								day.getDay() === 0 || day.getDay() === 6;

							return (
								<div
									key={dateStr}
									onClick={() =>
										handleDayClick(dateStr, dayLeaves)
									}
									style={{
										padding: "0.5rem",
										background: isWeekend
											? "var(--gray-50)"
											: "white",
										minHeight: "100px",
										cursor:
											dayLeaves.length > 0
												? "pointer"
												: "default",
										transition: "background 0.15s ease",
									}}
									onMouseEnter={(e) => {
										if (dayLeaves.length > 0) {
											e.currentTarget.style.background =
												"var(--primary-light)";
										}
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.background =
											isWeekend
												? "var(--gray-50)"
												: "white";
									}}
								>
									{/* Date number */}
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											marginBottom: "0.25rem",
										}}
									>
										<span
											style={{
												fontWeight: isToday
													? "700"
													: "500",
												fontSize: "0.875rem",
												color: isToday
													? "var(--primary)"
													: "var(--gray-700)",
												background: isToday
													? "var(--primary-light)"
													: "transparent",
												padding: isToday
													? "0.125rem 0.5rem"
													: "0",
												borderRadius:
													"var(--radius-sm)",
											}}
										>
											{day.getDate()}
										</span>
										{dayLeaves.length > 0 && (
											<span
												style={{
													fontSize: "0.7rem",
													background: "var(--danger)",
													color: "white",
													padding:
														"0.125rem 0.375rem",
													borderRadius:
														"var(--radius-sm)",
													fontWeight: "600",
												}}
											>
												{dayLeaves.length}
											</span>
										)}
									</div>

									{/* Leave entries (show up to 3) */}
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											gap: "2px",
										}}
									>
										{dayLeaves
											.slice(0, 3)
											.map((leave, i) => (
												<div
													key={leave.id}
													style={{
														fontSize: "0.7rem",
														padding:
															"0.125rem 0.25rem",
														background:
															"var(--danger-light, #fee2e2)",
														color: "var(--danger)",
														borderRadius:
															"var(--radius-sm)",
														whiteSpace: "nowrap",
														overflow: "hidden",
														textOverflow:
															"ellipsis",
													}}
													title={`${leave.first_name}${leave.reason ? ` - ${leave.reason}` : ""}`}
												>
													{leave.first_name}
												</div>
											))}
										{dayLeaves.length > 3 && (
											<div
												style={{
													fontSize: "0.65rem",
													color: "var(--gray-500)",
													textAlign: "center",
												}}
											>
												+{dayLeaves.length - 3} more
											</div>
										)}
									</div>
								</div>
							);
						})}
					</div>
				</div>

				{/* Legend */}
				<div
					style={{
						padding: "0.75rem 1.5rem",
						borderTop: "1px solid var(--gray-200)",
						display: "flex",
						gap: "1.5rem",
						fontSize: "0.875rem",
						color: "var(--gray-600)",
					}}
				>
					<span>
						<span
							style={{
								display: "inline-block",
								width: "12px",
								height: "12px",
								background: "var(--danger-light, #fee2e2)",
								borderRadius: "2px",
								marginRight: "0.5rem",
								verticalAlign: "middle",
							}}
						></span>
						Leave Day
					</span>
					<span>
						<span
							style={{
								display: "inline-block",
								width: "12px",
								height: "12px",
								background: "var(--primary-light)",
								borderRadius: "2px",
								marginRight: "0.5rem",
								verticalAlign: "middle",
							}}
						></span>
						Today
					</span>
					<span style={{ marginLeft: "auto" }}>
						Total leaves this month:{" "}
						<strong>
							{
								requests.filter((r) => {
									const reqDate = new Date(
										r.request_date + "T00:00:00",
									);
									return (
										reqDate.getMonth() ===
											viewMonth.getMonth() &&
										reqDate.getFullYear() ===
											viewMonth.getFullYear()
									);
								}).length
							}
						</strong>
					</span>
				</div>
			</div>

			{/* Day Detail Modal */}
			{showDetailModal && (
				<div
					className='modal-overlay'
					onClick={() => setShowDetailModal(false)}
				>
					<div
						className='modal'
						onClick={(e) => e.stopPropagation()}
						style={{ maxWidth: "500px" }}
					>
						<div className='modal-header'>
							<h2>
								Leaves on{" "}
								{new Date(
									selectedDayDate + "T00:00:00",
								).toLocaleDateString("en-US", {
									weekday: "long",
									month: "long",
									day: "numeric",
									year: "numeric",
								})}
							</h2>
							<button
								className='btn btn-sm btn-secondary'
								onClick={() => setShowDetailModal(false)}
							>
								×
							</button>
						</div>
						<div className='modal-body'>
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									gap: "0.75rem",
								}}
							>
								{selectedDayLeaves.map((leave) => (
									<div
										key={leave.id}
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											padding: "0.75rem",
											background: "var(--gray-50)",
											borderRadius: "var(--radius-md)",
											border: "1px solid var(--gray-200)",
										}}
									>
										<div>
											<div className='font-medium'>
												{leave.first_name}{" "}
											</div>
											{leave.employee_number && (
												<div className='text-xs text-muted'>
													#{leave.employee_number}
												</div>
											)}
											{leave.reason && (
												<div
													className='text-sm text-muted'
													style={{
														marginTop: "0.25rem",
													}}
												>
													{leave.reason}
												</div>
											)}
											<div
												style={{ marginTop: "0.25rem" }}
											>
												<span
													className={`badge ${getLeaveTypeBadgeClass(leave.leave_type)}`}
													style={{
														fontSize: "0.7rem",
													}}
												>
													{getLeaveTypeLabel(
														leave.leave_type,
													)}
												</span>
											</div>
										</div>
										<button
											className='btn btn-sm btn-danger'
											onClick={() => {
												handleDelete(leave.id);
												setSelectedDayLeaves((prev) =>
													prev.filter(
														(l) =>
															l.id !== leave.id,
													),
												);
												if (
													selectedDayLeaves.length <=
													1
												) {
													setShowDetailModal(false);
												}
											}}
										>
											Delete
										</button>
									</div>
								))}
							</div>
						</div>
						<div className='modal-footer'>
							<button
								className='btn btn-secondary'
								onClick={() => setShowDetailModal(false)}
							>
								Close
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Add Leave Modal with Calendar */}
			{showAddModal && (
				<div
					className='modal-overlay'
					onClick={() => setShowAddModal(false)}
				>
					<div
						className='modal'
						onClick={(e) => e.stopPropagation()}
						style={{ maxWidth: "500px" }}
					>
						<div className='modal-header'>
							<h2>Add Leave</h2>
							<button
								className='btn btn-sm btn-secondary'
								onClick={() => setShowAddModal(false)}
							>
								×
							</button>
						</div>
						<div className='modal-body'>
							<div className='form-group'>
								<label className='form-label'>Employee *</label>
								<select
									className='form-input'
									value={selectedEmployee}
									onChange={(e) =>
										handleEmployeeChange(e.target.value)
									}
								>
									<option value=''>Select Employee</option>
									{employees.map((emp) => (
										<option key={emp.id} value={emp.id}>
											{emp.first_name}
										</option>
									))}
								</select>
							</div>
							<div className='form-group'>
								<label className='form-label'>
									Select Leave Dates *
								</label>
								<p
									className='text-sm text-muted'
									style={{ marginBottom: "0.5rem" }}
								>
									Click on calendar days to select/deselect.
									Holidays and the employee's NWD can't be
									selected.
								</p>

								{/* Calendar Navigation */}
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										marginBottom: "0.5rem",
									}}
								>
									<button
										className='btn btn-sm btn-secondary'
										onClick={prevMonth}
									>
										◀
									</button>
									<span className='font-medium'>
										{calendarMonth.toLocaleDateString(
											"en-US",
											{ month: "long", year: "numeric" },
										)}
									</span>
									<button
										className='btn btn-sm btn-secondary'
										onClick={nextMonth}
									>
										▶
									</button>
								</div>

								{/* Calendar Grid */}
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "repeat(7, 1fr)",
										gap: "2px",
										background: "var(--gray-200)",
										border: "1px solid var(--gray-200)",
										borderRadius: "var(--radius-md)",
										overflow: "hidden",
									}}
								>
									{/* Day headers */}
									{[
										"Sun",
										"Mon",
										"Tue",
										"Wed",
										"Thu",
										"Fri",
										"Sat",
									].map((day) => (
										<div
											key={day}
											style={{
												padding: "0.5rem",
												textAlign: "center",
												fontWeight: "600",
												fontSize: "0.75rem",
												background: "var(--gray-100)",
											}}
										>
											{day}
										</div>
									))}

									{/* Calendar days */}
									{calendarDays.map((day, idx) => {
										if (!day) {
											return (
												<div
													key={`empty-${idx}`}
													style={{
														background: "white",
													}}
												/>
											);
										}
										const dateStr = formatDateStr(day);
										const isSelected =
											selectedDates.includes(dateStr);
										const isPast =
											day <
											new Date(
												new Date().setHours(0, 0, 0, 0),
											);
										const blockedReason = getBlockedReason(
											day,
											selectedEmployee,
										);
										const isDisabled =
											isPast || !!blockedReason;

										return (
											<div
												key={dateStr}
												title={
													blockedReason
														? blockedReason === "NWD"
															? "Employee's NWD"
															: "Holiday"
														: undefined
												}
												onClick={() =>
													!isDisabled &&
													toggleDate(dateStr)
												}
												style={{
													padding: "0.5rem",
													textAlign: "center",
													background: isSelected
														? "var(--primary-600)"
														: blockedReason
															? "var(--gray-100)"
															: "white",
													color: isSelected
														? "white"
														: isDisabled
															? "var(--gray-400)"
															: "inherit",
													cursor: isDisabled
														? "not-allowed"
														: "pointer",
													fontWeight: isSelected
														? "600"
														: "normal",
													textDecoration: "none",
													transition:
														"all 0.15s ease",
												}}
												onMouseEnter={(e) => {
													if (
														!isDisabled &&
														!isSelected
													) {
														e.target.style.background =
															"var(--primary-100)";
													}
												}}
												onMouseLeave={(e) => {
													if (!isSelected) {
														e.target.style.background =
															blockedReason
																? "var(--gray-100)"
																: "white";
													}
												}}
											>
												{day.getDate()}
												{blockedReason && (
													<div
														style={{
															fontSize: "0.6rem",
															fontWeight: "600",
															lineHeight: 1.1,
															pointerEvents: "none",
														}}
													>
														{blockedReason}
													</div>
												)}
											</div>
										);
									})}
								</div>

								{/* Selected dates summary */}
								{selectedDates.length > 0 && (
									<div style={{ marginTop: "0.75rem" }}>
										<span className='text-sm font-medium'>
											Selected: {selectedDates.length}{" "}
											day(s)
										</span>
										<div
											style={{
												display: "flex",
												flexWrap: "wrap",
												gap: "0.25rem",
												marginTop: "0.25rem",
											}}
										>
											{selectedDates.map((date) => (
												<span
													key={date}
													className='badge badge-primary'
													style={{
														cursor: "pointer",
													}}
													onClick={() =>
														toggleDate(date)
													}
												>
													{new Date(
														date + "T00:00:00",
													).toLocaleDateString(
														"en-US",
														{
															month: "short",
															day: "numeric",
														},
													)}{" "}
													×
												</span>
											))}
										</div>
									</div>
								)}
							</div>
							<div className='form-group'>
								<label className='form-label'>
									Reason (Optional)
								</label>
								<input
									type='text'
									className='form-input'
									value={reason}
									onChange={(e) => setReason(e.target.value)}
									placeholder='e.g., Family event, appointment'
								/>
							</div>
							<div className='form-group'>
								<label className='form-label'>Leave Type</label>
								<select
									className='form-input'
									value={leaveType}
									onChange={(e) =>
										setLeaveType(e.target.value)
									}
								>
									<option value='regular'>
										Annual Leave
									</option>
									<option value='sick'>Sick Leave</option>
								</select>
							</div>{" "}
						</div>
						<div className='modal-footer'>
							<button
								className='btn btn-secondary'
								onClick={() => {
									setShowAddModal(false);
									setSelectedEmployee("");
									setSelectedDates([]);
									setReason("");
									setLeaveType("regular");
								}}
							>
								Cancel
							</button>
							<button
								className='btn btn-primary'
								onClick={handleAddLeave}
							>
								Add Leave ({selectedDates.length} day
								{selectedDates.length !== 1 ? "s" : ""})
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default RequestList;
